import {
  BUNDLE_META_PREDICATE,
  isBundleDeclare,
  type BundleDeclarePayload,
} from './bundle-ops.js';
import { foldFactChainFull, type FactPayload, type FactStatus } from './fact-ops.js';
import { factKey } from './reveal-ops.js';

/**
 * Suggestion-set **bundle** fold (#251, 04-llm-role.md §14).
 *
 * Folds the op log into a **bundle registry** — `bundle_id → BundleRegistryEntry`
 * — built from two streams of ops:
 *
 *   1. `bundle.declare` meta-ops (subject = the `bun_…` id, predicate
 *      `__bundle`) supply the per-bundle generation metadata, once.
 *   2. Every fact op (`fact.create` / `fact.edit` / …) that carries a
 *      `bundle_id` in its payload is a **member** — its `(subject, predicate)`
 *      fact key joins the bundle named by that id.
 *
 * The registry is a rebuildable view of the op log — never authoritative,
 * exactly like the entities map / config record (ADR-0004; 02-architecture.md
 * §4.2). It is order-sensitive and assumes HLC-ordered input (the same contract
 * as `foldOpsIntoEntities` / `foldConfigOps`).
 *
 * **Bundle state is DERIVED, never an op.** There is no `bundle.set_state` —
 * `state` is computed from the live status of the bundle's member facts:
 *   - `open`     — at least one member is still an un-acted-on draft (a
 *                  `status: 'draft'` fact that hasn't been soft-deleted/rejected);
 *   - `resolved` — every member has been acted on (accepted → canon/pending,
 *                  shelved → hypothetical, or rejected → soft-deleted).
 *
 * A bundle with a `declare` but zero members is `resolved` (nothing pending);
 * a bundle with members but no declare op (e.g. a partial replay window) still
 * folds — the entry's `metadata` is `null` until/unless the declare op arrives.
 */

/** Open ↔ resolved, derived from member fact statuses (never an explicit op). */
export type BundleState = 'open' | 'resolved';

/** One bundle's folded registry entry. */
export interface BundleRegistryEntry {
  /** The bundle's id (`bun_…`). */
  bundle_id: string;
  /**
   * The generation metadata from the `bundle.declare` op, or `null` if no
   * declare op has folded yet (members can reference a bundle whose declare
   * is later in the stream, or absent in a partial window). Last-writer-wins
   * per id — a re-declare (e.g. a corrected mode) overwrites.
   */
  metadata: BundleDeclarePayload | null;
  /**
   * The `(subject, predicate)` fact keys of this bundle's members, in
   * first-seen op order, deduped. A member is any fact op carrying this
   * `bundle_id`; the same fact key appears once even across multiple ops
   * (create + edits) on its chain.
   */
  memberKeys: string[];
  /** Derived open/resolved (see module doc). */
  state: BundleState;
  /**
   * The `regenerated_from` link (`bun_…`) lifted from `metadata`, or `null`.
   * Surfaced at the top level so the regen-chain is walkable without reaching
   * into `metadata` (and is `null` when the declare hasn't folded yet).
   */
  regeneratedFrom: string | null;
}

/** The folded bundle registry: `bundle_id → entry`. */
export type BundleRegistry = Record<string, BundleRegistryEntry>;

/**
 * The minimal op shape the bundle fold reads. A structural subset of the op
 * envelope shared with the other folds — bundle routing needs only the
 * subject, predicate, and payload.
 */
interface BundleOpLike {
  subject: string;
  predicate: string;
  payload: unknown;
}

/**
 * Extract a `bundle_id` from a fact payload, if present. Only `fact.create` /
 * `fact.edit` carry meta fields; `set_status` / `soft_delete` / `restore` don't.
 * Returns the id string or `null`.
 */
function memberBundleId(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') return null;
  const kind = (payload as { kind?: unknown }).kind;
  if (kind !== 'fact.create' && kind !== 'fact.edit') return null;
  const id = (payload as { bundle_id?: unknown }).bundle_id;
  return typeof id === 'string' && id.length > 0 ? id : null;
}

/** Whether a payload is any fact op (carries a `fact.` kind). */
function isFactPayload(payload: unknown): payload is FactPayload {
  if (!payload || typeof payload !== 'object') return false;
  const kind = (payload as { kind?: unknown }).kind;
  return typeof kind === 'string' && kind.startsWith('fact.');
}

interface MemberAccumulator {
  /** Member fact keys in first-seen order (a key joins on its FIRST bundle-tagged op). */
  keyOrder: string[];
  /** Membership set for O(1) "is this key a member?" checks. */
  keySet: Set<string>;
}

/**
 * Whether a folded member fact still counts as an **un-acted-on draft** — the
 * sole condition that keeps a bundle `open`. A draft that has been soft-deleted
 * (the lossless reject path) IS acted on, so it does not keep the bundle open.
 * Any non-draft status (canon / pending / hypothetical) is likewise acted on.
 *
 * The chain folded here is the member's **full** `(subject, predicate)` op
 * chain — including the GM-authored `fact.set_status → canon` accept op (which
 * carries NO `bundle_id`) — so an accepted member correctly reads as non-draft.
 */
function memberKeepsOpen(chain: readonly FactPayload[]): boolean {
  const folded = foldFactChainFull(chain);
  if (!folded) return false; // no create → not a real member chain
  if (folded.soft_deleted) return false; // rejected (lossless soft-delete)
  return folded.status === ('draft' satisfies FactStatus);
}

/**
 * Fold an HLC-ordered op stream into the bundle registry (#251). Only
 * `bundle.declare` ops (on the reserved `__bundle` predicate) and fact ops
 * carrying a `bundle_id` participate; everything else is ignored, so this runs
 * over the full op log. `metadata` is last-writer-wins per id; members are
 * grouped by their carried `bundle_id` and `state` is derived from their
 * folded statuses.
 *
 * The caller delivers ops in HLC order; the fold is order-sensitive and does
 * not re-sort.
 */
export function foldBundles(ops: readonly BundleOpLike[]): BundleRegistry {
  const declares = new Map<string, BundleDeclarePayload>();
  const members = new Map<string, MemberAccumulator>();
  // The FULL fact chain per `(subject, predicate)` key — including the
  // GM-authored `set_status`/`soft_delete` accept/reject ops that carry no
  // `bundle_id`. A member's state is derived by folding its full chain, not
  // just its bundle-tagged ops.
  const factChains = new Map<string, FactPayload[]>();
  // Preserve bundle first-seen order (declare OR first member) for deterministic
  // registry key iteration.
  const bundleOrder: string[] = [];
  const seenBundle = new Set<string>();

  const touchBundle = (id: string): void => {
    if (seenBundle.has(id)) return;
    seenBundle.add(id);
    bundleOrder.push(id);
  };

  for (const op of ops) {
    const payload = op.payload;

    // 1. Bundle declaration (reserved predicate slot).
    if (op.predicate === BUNDLE_META_PREDICATE && isBundleDeclare(payload)) {
      // The declare op's subject is the canonical id; the payload field equals
      // it (DO-gated), but route on the payload id so a partial/replayed op is
      // self-describing. Last-writer-wins per id.
      declares.set(payload.bundle_id, payload);
      touchBundle(payload.bundle_id);
      continue;
    }

    // 2. Accumulate every fact op onto its `(subject, predicate)` chain (status
    //    derivation needs the full chain, accept ops included).
    if (isFactPayload(payload)) {
      const key = factKey(op.subject, op.predicate);
      let chain = factChains.get(key);
      if (!chain) {
        chain = [];
        factChains.set(key, chain);
      }
      chain.push(payload);

      // 3. A `bundle_id`-carrying fact op makes this key a member of that bundle.
      const bundleId = memberBundleId(payload);
      if (bundleId !== null) {
        touchBundle(bundleId);
        let acc = members.get(bundleId);
        if (!acc) {
          acc = { keyOrder: [], keySet: new Set() };
          members.set(bundleId, acc);
        }
        if (!acc.keySet.has(key)) {
          acc.keySet.add(key);
          acc.keyOrder.push(key);
        }
      }
    }
  }

  const registry: BundleRegistry = {};
  for (const id of bundleOrder) {
    const metadata = declares.get(id) ?? null;
    const acc = members.get(id);
    const memberKeys = acc ? [...acc.keyOrder] : [];
    // Derive state: open iff ANY member is still an un-acted-on draft. The
    // member's status comes from its FULL fact chain (accept/reject included).
    let anyOpen = false;
    if (acc) {
      for (const key of acc.keyOrder) {
        const chain = factChains.get(key);
        if (chain && memberKeepsOpen(chain)) {
          anyOpen = true;
          break;
        }
      }
    }
    registry[id] = {
      bundle_id: id,
      metadata,
      memberKeys,
      state: anyOpen ? 'open' : 'resolved',
      regeneratedFrom: metadata?.regenerated_from ?? null,
    };
  }

  return registry;
}
